// The quote flow's feature flags, keyed to the QUOTE — every device
// opening the same quote link sees the same variant. The page groups as
// `quote` with the URL's id — at PostHog init when the page loads on a
// quote URL (App.tsx), otherwise here the moment the client can take it
// — re-groups when the view returns a different canonical id, and NEVER
// calls posthog.identify with a quote id.
//
// Grouping happens in two steps on purpose. Membership comes first so the
// quote-keyed flags resolve before the cards render; it is also the
// online-flow exposure — opening a quote link IS seeing the online flow,
// dead ends included, so the flag is read once from the first quote-keyed
// flag load (PostHog counts an exposure only from a `$feature_flag_called`
// event, never from membership). The group is DESCRIBED (`subject_type`)
// only once a quote has actually come back — a dead end never learns what
// kind of quote it would have been.
//
// Fail open is the law here: a missing flag, a malformed payload, or an
// unreachable PostHog must always render everything. Flags COMMIT at the
// reveal — once the view has arrived (and any alias re-group settled),
// the committed set sticks for the life of the page; a flag landing late
// never collapses rendered cards. Unresolved flags get a bounded grace
// hold past view arrival before failing open: on a slow connection the
// view revalidates from cache (304) while PostHog pays a fresh TLS
// handshake, so without the hold the view wins the race every time.

import { useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import type { PostHog } from "posthog-js";

import type { QuoteType } from "./analytics";
import type { QuoteOfferScope } from "./api";
import { QUOTE_GROUP_TYPE } from "./quote-group";

const PACKAGES_FLAG = "quote-policy-packages";
const PAYMENT_OPTIONS_FLAG = "quote-policy-payment-options";
const DEFAULT_SELECTION_FLAG = "quote-policy-default-selection";
/** Whether a quiz informational screen holds on a loading animation
 * before revealing its content. `control` (the fail-open default) is
 * today's behavior; `test` reveals immediately. */
const QUIZ_RESPONSE_ANIMATION_FLAG = "quote-policy-quiz-response-animation";
/** What the schedule-call screen's "not ready" button says. `control`
 * (the fail-open default) is after-www's gate copy verbatim; `test` names
 * the quote screen the button actually leads to. */
const SCHEDULE_CALL_DEFERRAL_COPY_FLAG =
  "quote-policy-schedule-call-deferral-copy";
/** The online-flow cohort flag. Not read for a value here — its release
 * conditions put every quote group in `test` — but read once so PostHog
 * records the exposure (posthog-js sends `$feature_flag_called` once per
 * identity and value). */
const ONLINE_FLOW_FLAG = "pre-need-online-flow";
/** Whether the schedule-call station's booked face is the full
 * confirmation page. `control` (the fail-open default) is the
 * scheduler's own booked view. */
export const BOOKING_CONFIRMATION_FLAG = "quote-policy-booking-confirmation";

export const ALL_PLAN_SLUGS = ["simple", "value", "choice"];

/** How long an alias re-group may hold the reveal before failing open. */
const REGROUP_DEADLINE_MS = 2000;

/** How long unresolved flags may hold the reveal PAST view arrival
 * before failing open. On a fast connection flags resolve before the
 * view and this never engages. */
const FLAG_HOLD_MS = 1750;

/** What the quote screen preselects when the quote carries no saved
 * selection. UI-only — nothing is written anywhere until Continue. All
 * or nothing: the named pair preselects only when both are actually
 * offered; there is deliberately no stand-in. */
export interface QuoteDefaultSelection {
  plan_slug: string;
  option_slug: string;
}

/** An informational quiz screen's reveal: `control` holds a loading
 * animation before showing content (today's behavior everywhere else
 * this flag hasn't rolled out), `test` shows it immediately. */
export type QuizResponseAnimationVariant = "control" | "test";

/** The schedule-call screen's deferral button: `control` keeps after-www's
 * "Continue your application online", `test` says where it goes now. */
export type ScheduleCallDeferralCopyVariant = "control" | "test";

/** The schedule-call station's booked face: `control` is the scheduler's
 * own booked view, `test` is the full confirmation page. */
export type BookingConfirmationVariant = "control" | "test";

export interface QuoteFlags {
  /** Which packages render, in no particular order. */
  plan_slugs: string[];
  /** Offered option slugs per plan; null (or an absent plan key) shows
   * everything the server offers. */
  option_slugs_by_plan: Record<string, string[]> | null;
  /** Null = the flag is off (or unreachable): nothing preselects and the
   * customer chooses for themselves. */
  default_selection: QuoteDefaultSelection | null;
  quiz_response_animation: QuizResponseAnimationVariant;
  schedule_call_deferral_copy: ScheduleCallDeferralCopyVariant;
  booking_confirmation: BookingConfirmationVariant;
}

const FAIL_OPEN: QuoteFlags = {
  plan_slugs: ALL_PLAN_SLUGS,
  option_slugs_by_plan: null,
  // Unreachable flags fail toward customer choice, not toward a guess.
  default_selection: null,
  quiz_response_animation: "control",
  schedule_call_deferral_copy: "control",
  booking_confirmation: "control",
};

/** Reads a flag without counting an exposure. The offer-shaping flags are
 * read this way and their exposure is recorded separately, only for a
 * quote the flags actually apply to. */
const SILENT_READ = { send_event: false };

/** A usable read is the flag on AND a payload naming both slugs —
 * anything less preselects nothing, the same as the flag being off. */
function readDefaultSelection(posthog: PostHog): QuoteDefaultSelection | null {
  if (!posthog.getFeatureFlag(DEFAULT_SELECTION_FLAG, SILENT_READ)) {
    return null;
  }
  const payload = posthog.getFeatureFlagPayload(DEFAULT_SELECTION_FLAG);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const { plan_slug, option_slug } = payload as Record<string, unknown>;
  if (
    typeof plan_slug !== "string" ||
    plan_slug === "" ||
    typeof option_slug !== "string" ||
    option_slug === ""
  ) {
    return null;
  }
  return { plan_slug, option_slug };
}

function stringArray(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.every((entry) => typeof entry === "string")
    ? (value as string[])
    : null;
}

export function readFlags(posthog: PostHog): QuoteFlags {
  const planSlugs =
    stringArray(posthog.getFeatureFlagPayload(PACKAGES_FLAG)) ?? ALL_PLAN_SLUGS;

  let optionSlugs: Record<string, string[]> | null = null;
  if (posthog.getFeatureFlag(PAYMENT_OPTIONS_FLAG, SILENT_READ)) {
    const payload = posthog.getFeatureFlagPayload(PAYMENT_OPTIONS_FLAG);
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const entries = Object.entries(payload as Record<string, unknown>)
        .map(([plan, slugs]) => [plan, stringArray(slugs)] as const)
        .filter((entry): entry is [string, string[]] => entry[1] !== null);
      if (entries.length > 0) optionSlugs = Object.fromEntries(entries);
    }
  }

  return {
    plan_slugs: planSlugs,
    option_slugs_by_plan: optionSlugs,
    default_selection: readDefaultSelection(posthog),
    quiz_response_animation:
      posthog.getFeatureFlag(QUIZ_RESPONSE_ANIMATION_FLAG) === "test"
        ? "test"
        : "control",
    schedule_call_deferral_copy:
      posthog.getFeatureFlag(SCHEDULE_CALL_DEFERRAL_COPY_FLAG) === "test"
        ? "test"
        : "control",
    // Read without counting an exposure: only a family who books on the
    // schedule-call station is exposed (see
    // captureBookingConfirmationExposure).
    booking_confirmation:
      posthog.getFeatureFlag(BOOKING_CONFIRMATION_FLAG, {
        send_event: false,
      }) === "test"
        ? "test"
        : "control",
  };
}

/** Whether a rep's settings shaped the offer. An absent scope is not
 * customized. */
export function isOfferCustomized(
  offerScope: QuoteOfferScope | undefined,
): boolean {
  return offerScope?.customized === true;
}

/**
 * The flags as they apply to one offer. A rep-customized offer is already
 * the exact set to show, so the package, payment-option and default-selection
 * flags stand down (all plans, all options, nothing preselected). Applied at
 * the point of use: the committed flags can freeze before the real view
 * arrives, so the offer in hand decides, not the commit.
 */
export function flagsForOffer(
  flags: QuoteFlags,
  offerScope: QuoteOfferScope | undefined,
): QuoteFlags {
  if (!isOfferCustomized(offerScope)) return flags;
  return {
    ...flags,
    plan_slugs: FAIL_OPEN.plan_slugs,
    option_slugs_by_plan: FAIL_OPEN.option_slugs_by_plan,
    default_selection: FAIL_OPEN.default_selection,
  };
}

/** Whether the offer-shaping flags' exposure is due: the flags have
 * committed, the offer is known (null while unknown), and it is not
 * rep-customized. */
export function offerExposureDue(
  committed: boolean,
  offerCustomized: boolean | null,
): boolean {
  return committed && offerCustomized === false;
}

/** The quote group's descriptive properties. Unknown values are omitted
 * rather than sent as null, which would overwrite what other devices
 * sharing the group have recorded. */
export function quoteGroupProperties(args: {
  subjectType: QuoteType;
  quoteOrigin: string | null;
  offerCustomized: boolean | null;
}): Record<string, string | boolean> {
  return {
    subject_type: args.subjectType,
    ...(args.offerCustomized === null
      ? {}
      : { rep_customized: args.offerCustomized }),
    ...(args.quoteOrigin ? { quote_origin: args.quoteOrigin } : {}),
  };
}

/** Records the exposure of the offer-shaping flags that count one
 * (posthog-js sends `$feature_flag_called` once per identity and value). */
export function captureOfferFlagExposure(
  posthog: Pick<PostHog, "getFeatureFlag">,
): void {
  posthog.getFeatureFlag(PAYMENT_OPTIONS_FLAG);
  posthog.getFeatureFlag(DEFAULT_SELECTION_FLAG);
}

/**
 * Whether to present the quote as a single plan. Derived from what
 * actually renders rather than asked for separately: a screen offering
 * one package IS a single-plan screen, and `quote-policy-packages` —
 * whose variant payload names the slugs — is the one control over
 * that. A second flag could only ever disagree with this count, and
 * every way it could disagree is a broken screen: the treatment worn
 * by a field of three, or one lone card in a grid built for three.
 *
 * This also catches a market the server prices a single package for,
 * with no flag involved. That is the same screen and wants the same
 * treatment.
 */
export function singlePlanTreatment(shown: { slug: string }[]): boolean {
  return shown.length === 1;
}

/**
 * Groups, resolves and commits the quote flow's flags.
 *
 * `skeletonPlanSlugs` is the pre-commit read — which package cards to
 * skeleton, null while the packages flag hasn't resolved (the cards
 * region shows a plain spinner). `skeletonSinglePlan` is the same read
 * for the treatment, so the outlines painted before commit are the
 * shape the cards will actually take — it counts the payload's slugs,
 * the best guess available before the server has said what it actually
 * priced. `flags` is the committed set:
 * null until the view has arrived AND any alias re-group has settled
 * (or its deadline passed), then fixed forever.
 */
export function useQuoteFlags(args: {
  urlQuoteId: string | null;
  /** The view's quote id, once loaded — the canonical identity. */
  canonicalQuoteId: string | null;
  /** True once the view request settled (loaded OR failed) — the flag
   * deadline. */
  viewSettled: boolean;
  /** What kind of quote came back — null until one actually has. Describes
   * the group. */
  subjectType: QuoteType | null;
  /** Which channel created the quote — describes the group alongside its
   * kind, so a breakdown by origin needs no per-event property. Null
   * until a quote comes back. */
  quoteOrigin: string | null;
  /** Whether the quote's offer was shaped by a rep; null until the offer
   * is known. The offer-shaping flags record no exposure for a customized
   * quote, nor before it is known. */
  offerCustomized: boolean | null;
}): {
  skeletonPlanSlugs: string[] | null;
  skeletonSinglePlan: boolean;
  flags: QuoteFlags | null;
} {
  const posthog = usePostHog();
  const [resolved, setResolved] = useState<QuoteFlags | null>(null);
  const [flags, setFlags] = useState<QuoteFlags | null>(null);
  // The provider initializes the client in a PARENT effect — which runs
  // after this hook's — so a group() fired straight from mount lands on
  // an uninitialized client and is dropped. Everything below waits for
  // the client to actually be loaded.
  const [clientReady, setClientReady] = useState(false);
  // How many times onFeatureFlags has fired — the alias path waits for a
  // fire AFTER its re-group rather than trusting pre-group values.
  const [flagLoads, setFlagLoads] = useState(0);
  // Set while an alias re-group holds the commit: cleared by the next
  // flag load or by its own deadline, whichever comes first.
  const [regroupWait, setRegroupWait] = useState<{
    sinceLoad: number;
  } | null>(null);
  // True once the grace hold for unresolved flags has run out.
  const [holdExpired, setHoldExpired] = useState(false);
  const groupedIdRef = useRef<string | null>(null);
  // Which group (id + kind) has been described — a description is sent
  // once per group, again only if the canonical id turns out to differ.
  const describedRef = useRef<string | null>(null);
  // Set once the member group is registered: the exposure is read on the
  // first flag load that was quote-keyed, never from ungrouped values.
  const [exposureWait, setExposureWait] = useState<{
    sinceLoad: number;
  } | null>(null);
  // The live flag-load count, for effects that must read it at the moment
  // they run rather than from the render that scheduled them.
  const flagLoadsRef = useRef(0);

  useEffect(() => {
    if (posthog.__loaded) {
      setClientReady(true);
      return;
    }
    const poll = setInterval(() => {
      if (posthog.__loaded) {
        clearInterval(poll);
        setClientReady(true);
      }
    }, 50);
    return () => clearInterval(poll);
  }, [posthog]);

  useEffect(() => {
    if (!clientReady) return;
    const unsubscribe = posthog.onFeatureFlags(() => {
      setResolved(readFlags(posthog));
      flagLoadsRef.current += 1;
      setFlagLoads((count) => count + 1);
    });
    return unsubscribe;
  }, [posthog, clientReady]);

  // Membership with the URL id — canonical except for resubmission
  // aliases. A page loaded on a quote URL was grouped at init (App.tsx),
  // so every flag load so far was quote-keyed and the exposure may read
  // from the first. Reached by in-app navigation instead, the group
  // registers here and the exposure waits for the reload that triggers —
  // but only once the client's own first flags request has landed:
  // posthog-js queues a reload asked for mid-flight behind that request,
  // so grouping earlier would make the next load the stale, ungrouped one.
  useEffect(() => {
    if (!clientReady || !args.urlQuoteId || groupedIdRef.current !== null) {
      return;
    }
    if (posthog.getGroups()[QUOTE_GROUP_TYPE] === args.urlQuoteId) {
      groupedIdRef.current = args.urlQuoteId;
      setExposureWait({ sinceLoad: 0 });
      return;
    }
    if (!posthog.featureFlags.hasLoadedFlags) return;
    groupedIdRef.current = args.urlQuoteId;
    setExposureWait({ sinceLoad: flagLoadsRef.current });
    posthog.group(QUOTE_GROUP_TYPE, args.urlQuoteId);
    // Re-runs on each flag load so the in-app path groups once flags land.
  }, [posthog, clientReady, args.urlQuoteId, flagLoads]);

  // The canonical id surfaced and differs from what we grouped as — a
  // resubmission alias. Re-group and hold the commit for one more flag
  // load (bounded by the deadline), so the revealed variant is the
  // canonical group's.
  useEffect(() => {
    if (!clientReady || !args.canonicalQuoteId || flags !== null) return;
    if (groupedIdRef.current === args.canonicalQuoteId) return;
    groupedIdRef.current = args.canonicalQuoteId;
    setRegroupWait({ sinceLoad: flagLoads });
    posthog.group(QUOTE_GROUP_TYPE, args.canonicalQuoteId);
    const deadline = setTimeout(() => {
      setRegroupWait(null);
    }, REGROUP_DEADLINE_MS);
    return () => clearTimeout(deadline);
    // The flag-load count is read at re-group time, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog, clientReady, args.canonicalQuoteId, flags]);

  // A quote came back: describe the group it belongs to. posthog.group
  // with properties sends the `$groupidentify` and overrides the group's
  // properties for flag evaluation on this device, then reloads flags.
  useEffect(() => {
    const groupedId = groupedIdRef.current;
    if (!clientReady || !args.subjectType || !groupedId) return;
    const description = `${groupedId}:${args.subjectType}:${args.quoteOrigin ?? ""}:${args.offerCustomized}`;
    if (describedRef.current === description) return;
    describedRef.current = description;
    posthog.group(
      QUOTE_GROUP_TYPE,
      groupedId,
      quoteGroupProperties({
        subjectType: args.subjectType,
        quoteOrigin: args.quoteOrigin,
        offerCustomized: args.offerCustomized,
      }),
    );
    // The canonical id is tracked so a pre-commit alias re-group gets
    // described too. After the commit the re-group deliberately no longer
    // happens, and the group this device is in stays the one described.
  }, [
    posthog,
    clientReady,
    args.subjectType,
    args.quoteOrigin,
    args.canonicalQuoteId,
    args.offerCustomized,
  ]);

  // The offer-shaping flags' exposure, recorded after the flags commit and
  // only for a quote whose offer is known not to be rep-customized. The
  // effect re-runs whenever `resolved` changes (each flag load);
  // posthog-js dedupes by identity and value.
  useEffect(() => {
    if (resolved === null) return;
    if (!offerExposureDue(flags !== null, args.offerCustomized)) return;
    captureOfferFlagExposure(posthog);
  }, [posthog, flags, resolved, args.offerCustomized]);

  // The first quote-keyed flag load is in — record the exposure.
  useEffect(() => {
    if (!exposureWait || flagLoads <= exposureWait.sinceLoad) return;
    setExposureWait(null);
    posthog.getFeatureFlag(ONLINE_FLOW_FLAG);
  }, [posthog, exposureWait, flagLoads]);

  // The view has settled but the flags haven't resolved at all — start
  // the grace clock. Resolution cancels it (the cleanup); expiry lets
  // the commit fail open.
  useEffect(() => {
    if (!args.viewSettled || flags !== null || resolved !== null) return;
    const timer = setTimeout(() => setHoldExpired(true), FLAG_HOLD_MS);
    return () => clearTimeout(timer);
  }, [args.viewSettled, flags, resolved]);

  // The commit: view settled + no re-group in flight + flags resolved
  // (or their grace hold spent) → the flags freeze, failing open if they
  // never resolved in time.
  useEffect(() => {
    if (!args.viewSettled || flags !== null) return;
    if (regroupWait && flagLoads <= regroupWait.sinceLoad) return;
    if (resolved === null && !holdExpired) return;
    setFlags(resolved ?? FAIL_OPEN);
  }, [args.viewSettled, flags, flagLoads, regroupWait, resolved, holdExpired]);

  return {
    skeletonPlanSlugs: resolved?.plan_slugs ?? null,
    skeletonSinglePlan: resolved
      ? singlePlanTreatment(resolved.plan_slugs.map((slug) => ({ slug })))
      : false,
    flags,
  };
}
